// Batch C (AB#2195, G-41) — Super Admin suspends or reinstates a salon.
//
// Suspending needs a reason and says what happens: customers can no longer find
// or book the salon, the owner is told, existing bookings are NOT cancelled. The
// salon's upcoming open bookings are listed underneath so support can work
// through them (each one can be cancelled with a reason). Reinstating makes the
// salon live again. A refusal is explained from its stable errorCode.

import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/Button/Button';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import { useAdminOpenBookings, useAdminSalonStatus } from '../../../hooks/useAdminSupport';
import { formatAppointmentDateTime } from '../../../lib/appointmentTime';
import type { AdminOwnerBusinessRef, AdminSupportAppointmentDto } from '../../../services/api';
import { normalizeApiError } from '../../../types/api-errors';
import AdminCancelBookingModal from './AdminCancelBookingModal';
import { useBookingStatusLabel } from './bookingStatus';
import styles from './Support.module.css';
import { useDialogDismiss } from './useDialogDismiss';

export const SUSPEND_REASON_MAX = 500;

interface SalonStatusModalProps {
    ownerName: string;
    businesses: AdminOwnerBusinessRef[];
    onClose: () => void;
    /** Called after a status change so the owners list can refresh. */
    onChanged: () => void;
}

const REFUSAL_KEYS: Record<string, string> = {
    'ADMIN.SUSPEND_INVALID_STATE': 'superadmin.salonStatus.error.notLive',
    'ADMIN.REINSTATE_INVALID_STATE': 'superadmin.salonStatus.error.notSuspended',
    'ADMIN.SUSPENSION_REASON_REQUIRED': 'superadmin.salonStatus.error.reasonRequired',
    'ADMIN.SUSPENSION_REASON_TOO_LONG': 'superadmin.support.cancel.error.reasonTooLong',
};

const STATUS_KEYS: Record<string, string> = {
    Approved: 'superadmin.salonStatus.status.live',
    Suspended: 'superadmin.salonStatus.status.suspended',
    PendingApproval: 'superadmin.salonStatus.status.pending',
    Draft: 'superadmin.salonStatus.status.draft',
    Rejected: 'superadmin.salonStatus.status.rejected',
};

export function useSalonStatusLabel() {
    const { t } = useLanguage();
    return (status: string): string => (STATUS_KEYS[status] ? t(STATUS_KEYS[status]) : status || '—');
}

const SalonStatusModal: React.FC<SalonStatusModalProps> = ({ ownerName, businesses, onClose, onChanged }) => {
    const { t, language } = useLanguage();
    const { showToast } = useToast();
    const salonStatusLabel = useSalonStatusLabel();
    const bookingStatusLabel = useBookingStatusLabel();
    const { suspend, reinstate } = useAdminSalonStatus();

    const [businessId, setBusinessId] = useState<number>(businesses[0]?.businessId ?? 0);
    // Local copy of the status, so the dialog reflects a change before the owners
    // list has been refetched.
    const [statusOverride, setStatusOverride] = useState<Record<number, string>>({});
    const [reason, setReason] = useState('');
    const [errorKey, setErrorKey] = useState<string | null>(null);
    const [cancelTarget, setCancelTarget] = useState<AdminSupportAppointmentDto | null>(null);

    const business = useMemo(
        () => businesses.find((b) => b.businessId === businessId) ?? businesses[0],
        [businesses, businessId],
    );
    const status = statusOverride[business?.businessId ?? 0] ?? business?.status ?? '';
    const isLive = status === 'Approved';
    const isSuspended = status === 'Suspended';
    const submitting = suspend.isPending || reinstate.isPending;

    // Open bookings matter once the salon is suspended (what is left to handle).
    const openBookings = useAdminOpenBookings(isSuspended && business ? business.businessId : null);
    // Escape closes only the top dialog, so it never closes this one while the
    // cancel dialog is open over it.
    const dialogRef = useDialogDismiss<HTMLDivElement>(() => {
        if (!suspend.isPending && !reinstate.isPending) onClose();
    });

    useEffect(() => {
        setReason('');
        setErrorKey(null);
    }, [businessId]);

    if (!business) return null;

    const handleClose = () => {
        if (!submitting) onClose();
    };
    // A stray click outside must not throw away a reason the admin has typed.
    const handleBackdrop = () => {
        if (!reason.trim()) handleClose();
    };

    const fail = (err: unknown) => {
        const { code, status: httpStatus } = normalizeApiError(err);
        setErrorKey(
            REFUSAL_KEYS[code] ??
                (httpStatus === 404 ? 'superadmin.salonStatus.error.notFound' : 'superadmin.salonStatus.error.generic'),
        );
    };

    const handleSuspend = async () => {
        const trimmed = reason.trim();
        if (!trimmed) {
            setErrorKey('superadmin.salonStatus.error.reasonRequired');
            return;
        }
        setErrorKey(null);
        try {
            await suspend.mutateAsync({ businessId: business.businessId, reason: trimmed });
            setStatusOverride((prev) => ({ ...prev, [business.businessId]: 'Suspended' }));
            setReason('');
            showToast(t('superadmin.salonStatus.toast.suspended'), 'success');
            onChanged();
        } catch (err) {
            fail(err);
        }
    };

    const handleReinstate = async () => {
        setErrorKey(null);
        try {
            await reinstate.mutateAsync({ businessId: business.businessId });
            setStatusOverride((prev) => ({ ...prev, [business.businessId]: 'Approved' }));
            setReason('');
            showToast(t('superadmin.salonStatus.toast.reinstated'), 'success');
            onChanged();
        } catch (err) {
            fail(err);
        }
    };

    const rows = openBookings.data ?? [];

    return (
        <>
            <div className={styles.modalOverlay} role="presentation" onClick={handleBackdrop}>
                <div
                    ref={dialogRef}
                    tabIndex={-1}
                    className={`${styles.modal} ${styles.modalWide}`}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="salon-status-title"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className={styles.modalHeader}>
                        <h2 id="salon-status-title" className={styles.modalTitle}>
                            {t('superadmin.salonStatus.title')}
                        </h2>
                        <button
                            type="button"
                            className={styles.closeButton}
                            onClick={handleClose}
                            aria-label={t('superadmin.bookings.close')}
                        >
                            &times;
                        </button>
                    </div>

                    <div className={styles.modalContent}>
                        {businesses.length > 1 && (
                            <div className={styles.field}>
                                <label htmlFor="salon-status-business" className={styles.label}>
                                    {t('superadmin.bookings.business')}
                                </label>
                                <select
                                    id="salon-status-business"
                                    className={styles.select}
                                    value={business.businessId}
                                    onChange={(e) => setBusinessId(Number(e.target.value))}
                                    disabled={submitting}
                                >
                                    {businesses.map((b) => (
                                        <option key={b.businessId} value={b.businessId}>
                                            {b.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <dl className={styles.summary}>
                            <dt>{t('superadmin.salonStatus.owner')}</dt>
                            <dd><bdi>{ownerName}</bdi></dd>
                            <dt>{t('superadmin.support.col.salon')}</dt>
                            <dd><bdi>{business.name}</bdi></dd>
                            <dt>{t('superadmin.salonStatus.current')}</dt>
                            <dd>
                                <span className={`${styles.statusBadge} ${isSuspended ? styles.statusBadgeWarning : ''}`}>
                                    {salonStatusLabel(status)}
                                </span>
                            </dd>
                            {isSuspended && business.suspensionReason && !statusOverride[business.businessId] ? (
                                <>
                                    <dt>{t('approval.banner.reason')}</dt>
                                    <dd><bdi>{business.suspensionReason}</bdi></dd>
                                </>
                            ) : null}
                        </dl>

                        {isLive && (
                            <>
                                <p className={styles.body}>{t('superadmin.salonStatus.suspend.body')}</p>
                                <div className={styles.field}>
                                    <label htmlFor="salon-suspend-reason" className={styles.label}>
                                        {t('superadmin.support.reason.label')}
                                    </label>
                                    <textarea
                                        id="salon-suspend-reason"
                                        className={styles.textarea}
                                        value={reason}
                                        maxLength={SUSPEND_REASON_MAX}
                                        onChange={(e) => setReason(e.target.value)}
                                        placeholder={t('superadmin.salonStatus.suspend.reason.placeholder')}
                                        dir="auto"
                                        disabled={submitting}
                                        aria-describedby="salon-suspend-reason-hint"
                                    />
                                    <span id="salon-suspend-reason-hint" className={styles.hint}>
                                        {t('superadmin.salonStatus.suspend.reason.hint')}
                                    </span>
                                    <span className={styles.counter}>{reason.length}/{SUSPEND_REASON_MAX}</span>
                                </div>
                            </>
                        )}

                        {isSuspended && (
                            <p className={styles.body}>{t('superadmin.salonStatus.reinstate.body')}</p>
                        )}

                        {!isLive && !isSuspended && (
                            <p className={styles.notice}>{t('superadmin.salonStatus.notLiveNote')}</p>
                        )}

                        {errorKey && (
                            <p className={styles.error} role="alert">
                                {t(errorKey)}
                            </p>
                        )}

                        {isSuspended && (
                            <section aria-labelledby="salon-open-bookings-title">
                                <h3 id="salon-open-bookings-title" className={styles.label}>
                                    {t('superadmin.salonStatus.openBookings.title')}
                                </h3>
                                <p className={styles.hint}>{t('superadmin.salonStatus.openBookings.hint')}</p>
                                {openBookings.isLoading && (
                                    <p className={styles.stateText}>{t('superadmin.bookings.loading')}</p>
                                )}
                                {openBookings.isError && (
                                    <p className={styles.error} role="alert">{t('superadmin.bookings.error')}</p>
                                )}
                                {!openBookings.isLoading && !openBookings.isError && rows.length === 0 && (
                                    <p className={styles.stateText}>{t('superadmin.salonStatus.openBookings.empty')}</p>
                                )}
                                {rows.length > 0 && (
                                    <div className={styles.tableWrapper}>
                                        <table className={styles.table}>
                                            <thead>
                                                <tr>
                                                    <th>{t('superadmin.support.col.booking')}</th>
                                                    <th>{t('superadmin.bookings.col.dateTime')}</th>
                                                    <th>{t('superadmin.bookings.col.service')}</th>
                                                    <th>{t('superadmin.bookings.col.customer')}</th>
                                                    <th>{t('superadmin.bookings.col.phone')}</th>
                                                    <th>{t('superadmin.bookings.col.status')}</th>
                                                    <th>{t('superadmin.support.col.actions')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {rows.map((a) => (
                                                    <tr key={a.appointmentId}>
                                                        <td><bdi>#{a.appointmentId}</bdi></td>
                                                        <td>{a.appointmentDate ? formatAppointmentDateTime(a.appointmentDate, language) : '—'}</td>
                                                        <td><bdi>{a.serviceName || '—'}</bdi></td>
                                                        <td><bdi>{a.customerName || '—'}</bdi></td>
                                                        <td><bdi>{a.customerPhone || '—'}</bdi></td>
                                                        <td>
                                                            <span className={styles.statusBadge}>{bookingStatusLabel(a.status)}</span>
                                                        </td>
                                                        <td>
                                                            <Button
                                                                label={t('superadmin.support.action.cancel')}
                                                                onClick={() => setCancelTarget(a)}
                                                                variant="secondary"
                                                                size="small"
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </section>
                        )}
                    </div>

                    <div className={styles.modalActions}>
                        <Button
                            label={t('superadmin.bookings.close')}
                            onClick={handleClose}
                            variant="secondary"
                            disabled={submitting}
                        />
                        {isLive && (
                            <Button
                                label={t(suspend.isPending ? 'superadmin.salonStatus.action.suspending' : 'superadmin.salonStatus.action.suspend')}
                                onClick={handleSuspend}
                                variant="primary"
                                disabled={submitting || !reason.trim()}
                            />
                        )}
                        {isSuspended && (
                            <Button
                                label={t(reinstate.isPending ? 'superadmin.salonStatus.action.reinstating' : 'superadmin.salonStatus.action.reinstate')}
                                onClick={handleReinstate}
                                variant="primary"
                                disabled={submitting}
                            />
                        )}
                    </div>
                </div>
            </div>

            {cancelTarget && (
                <AdminCancelBookingModal
                    booking={{ ...cancelTarget, businessName: business.name }}
                    onClose={() => setCancelTarget(null)}
                />
            )}
        </>
    );
};

export default SalonStatusModal;
